import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { getMediaAsset } from "api/endpoints/media";
import useMediaUpload from "hooks/useMediaUpload";

/**
 * Batch 2.1 — <MediaPicker>. Wraps useMediaUpload for a single media asset.
 *
 * Controlled by `value` (a media_asset_id) / `onChange`. On an edit form the
 * parent only has the id, so the picker hydrates display info via
 * getMediaAsset. `visibility` decides public vs. gated (private) storage.
 */
function MediaPicker({ value, onChange, websiteId, visibility, accept, label }) {
  const { upload, status, progress } = useMediaUpload({ websiteId });
  const [asset, setAsset] = useState(null);
  const [loadingAsset, setLoadingAsset] = useState(false);
  const inputRef = useRef(null);

  // Hydrate display info for an existing value — edit forms supply only the id.
  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setAsset(null);
      return undefined;
    }
    setLoadingAsset(true);
    getMediaAsset(websiteId, value)
      .then((data) => {
        if (!cancelled) setAsset(data);
      })
      .catch(() => {
        if (!cancelled) setAsset(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingAsset(false);
      });
    return () => {
      cancelled = true;
    };
  }, [value, websiteId]);

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    const altText = window.prompt("Alt text (accessibility & SEO):", "") || "";
    try {
      const uploaded = await upload(file, { altText, visibility });
      setAsset(uploaded);
      onChange(uploaded.media_asset_id);
    } catch {
      window.alert("Upload failed. Please try again.");
    }
  };

  const clear = () => {
    setAsset(null);
    onChange(null);
  };

  const isImage = asset?.mime_type?.startsWith("image/");
  const uploading = status === "uploading";

  return (
    <MDBox>
      {label && (
        <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
          {label}
        </MDTypography>
      )}
      <input ref={inputRef} type="file" accept={accept} hidden onChange={handleFile} />
      <MDBox
        display="flex"
        alignItems="center"
        gap={2}
        p={2}
        sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: 1 }}
      >
        {uploading && (
          <MDBox display="flex" alignItems="center" gap={1}>
            <CircularProgress size={20} />
            <MDTypography variant="caption">Uploading… {progress}%</MDTypography>
          </MDBox>
        )}

        {!uploading && value && (
          <>
            {isImage && asset?.file_url ? (
              <Box
                component="img"
                src={asset.file_url}
                alt={asset.alt_text || ""}
                sx={{ width: 56, height: 56, objectFit: "cover", borderRadius: 1 }}
              />
            ) : (
              <Icon fontSize="large" color="info">
                {isImage ? "image" : "description"}
              </Icon>
            )}
            <MDTypography variant="button" sx={{ flex: 1, wordBreak: "break-word" }}>
              {loadingAsset ? "Loading…" : asset?.original_filename || "Asset selected"}
            </MDTypography>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={() => inputRef.current?.click()}
            >
              Replace
            </MDButton>
            <MDButton size="small" variant="text" color="error" onClick={clear}>
              Clear
            </MDButton>
          </>
        )}

        {!uploading && !value && (
          <>
            <Icon fontSize="large" color="disabled">
              add_photo_alternate
            </Icon>
            <MDTypography variant="button" color="text" sx={{ flex: 1 }}>
              No file selected
            </MDTypography>
            <MDButton
              size="small"
              variant="outlined"
              color="info"
              onClick={() => inputRef.current?.click()}
            >
              Upload
            </MDButton>
          </>
        )}
      </MDBox>
    </MDBox>
  );
}

MediaPicker.defaultProps = {
  value: null,
  websiteId: undefined,
  visibility: "public",
  accept: undefined,
  label: null,
};

MediaPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  websiteId: PropTypes.string,
  visibility: PropTypes.oneOf(["public", "private"]),
  accept: PropTypes.string,
  label: PropTypes.string,
};

export default MediaPicker;
